/**
 * MeslekSecici.jsx — bir işe alımın MESLEK KÜMESİNİ düzenleyen çoklu seçim (backlog #372, backend #371).
 *
 * Seçili meslekler kaldırılabilir kutucuklar, altında "Meslek ekle" açılır listesi. Kümenin sahibi tek bir işe alım
 * (firma × fuar): `PATCH /{personId}/meslek { meslekIds }` o işe alımın TÜM kümesini değiştirir, bu yüzden çağıran
 * kümeyi bütün olarak gönderir. Kişinin o fuardaki ÖTEKİ firmaların verdiği meslekleri (`buFirmada === false`) kişinin
 * belge borcuna sayılır ama bu işlemle değişmez: soluk, kilitli kutucuk olarak yalnızca gösterilir (firma adı verilmez).
 *
 * Metinler `components:meslekSecici` — Personel Ekle penceresi firma portalında da açıldığı için çevrilir.
 *
 * @param secili     düzenlenen kümenin meslek id'leri
 * @param onChange   (id[]) => void
 * @param katalog    `getMeslekList()` satırları (`{ meslekId, ad, kaldirildi }`) — eklenebilecek seçenekler
 * @param adlar      id → ad; katalogda olmayan (kaldırılmış) bir mevcut mesleğin adını çözmek için
 * @param digerleri  [{ meslekId, meslekAdi }] — başka firmanın verdikleri (salt gösterim)
 */
import React from "react";
import { Badge, Input } from "reactstrap";
import { Lock, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../i18n";
import { mesleklerKategoriyeGore } from "./meslekler";

/**
 * #466 (#465'in seçici yarısı): seçilebilir mesleklerin <option> listesi, kategoriye göre <optgroup>larda (#463 ağacı). Kategorinin
 * kendisi de seçilebilir (kişi bir kategoriyi tutabilir, kendi kuralı olabilir): grubun ilk satırı. Kategorisiz meslekler en sonda.
 * `haric`: listeden çıkarılacak id'ler (kişide zaten olanlar).
 */
export function MeslekSecenekleri({ katalog, haric = [] }) {
  const { t } = useTranslation("components");
  const gruplar = mesleklerKategoriyeGore(katalog, { haric, dil: currentLocaleTag() });
  return gruplar.map((g) => (
    <optgroup key={g.kategori ? g.kategori.meslekId : "kategorisiz"} label={g.kategori ? g.kategori.ad : t("meslekSecici.kategorisiz")}>
      {g.kategori && !g.kategoriHaric && (
        <option value={String(g.kategori.meslekId)}>{t("meslekSecici.kategoriKendisi", { ad: g.kategori.ad })}</option>
      )}
      {g.meslekler.map((m) => <option key={m.meslekId} value={String(m.meslekId)}>{m.ad}</option>)}
    </optgroup>
  ));
}

export default function MeslekSecici({ secili, onChange, katalog = [], adlar = {}, digerleri = [], disabled = false }) {
  const { t } = useTranslation("components");
  const seciliSet = new Set(secili.map(Number));
  const secenekler = katalog.filter((m) => m.meslekId !== 0 && !m.kaldirildi && !seciliSet.has(Number(m.meslekId)));
  const satirOf = (id) => katalog.find((m) => Number(m.meslekId) === Number(id));
  const adOf = (id) => satirOf(id)?.ad ?? adlar[id] ?? `#${id}`;
  // Katalog yalnızca sunumdaki meslekleri getirir (ya da kaldırılmışları işaretli getirir): listede olmayan = kaldırılmış.
  const kaldirilmis = (id) => { const m = satirOf(id); return m ? m.kaldirildi === true : katalog.length > 0; };

  return (
    <div>
      <div className="d-flex flex-wrap gap-1 mb-2">
        {secili.length === 0 && digerleri.length === 0 && <span className="text-muted small">{t("meslekSecici.belirtilmedi")}</span>}
        {secili.map((id) => (
          <Badge key={id} color="light" className="text-body border fw-normal d-inline-flex align-items-center gap-1 py-1">
            {adOf(id)}{kaldirilmis(id) ? ` ${t("meslekSecici.kaldirilmis")}` : ""}
            <button
              type="button" className="btn btn-sm p-0 border-0 lh-1 text-muted" disabled={disabled}
              title={t("meslekSecici.kaldir")} aria-label={t("meslekSecici.kaldirAria", { ad: adOf(id) })}
              onClick={() => onChange(secili.filter((x) => Number(x) !== Number(id)))}
            >
              <X size={12} />
            </button>
          </Badge>
        ))}
        {digerleri.map((m) => (
          <Badge
            key={`diger-${m.meslekId}`} color="light" className="text-muted border fw-normal d-inline-flex align-items-center gap-1 py-1"
            style={{ opacity: 0.7 }} title={t("meslekSecici.baskaFirma")}
          >
            <Lock size={11} />{m.meslekAdi}
          </Badge>
        ))}
      </div>
      <Input
        type="select" bsSize="sm" value="" disabled={disabled || secenekler.length === 0}
        onChange={(e) => { if (e.target.value) onChange([...secili, Number(e.target.value)]); }}
      >
        <option value="">{t("meslekSecici.meslekEkle")}</option>
        {/* WAS (until #466): secenekler.map((m) => <option key={m.meslekId} value={String(m.meslekId)}>{m.ad}</option>) — now grouped by category */}
        <MeslekSecenekleri katalog={katalog} haric={[...seciliSet]} />
      </Input>
    </div>
  );
}
